/**
 * Vista Lista del día — proto agenda-ux-v1 (.list-card): fila por turno
 * ordenada por hora, pensada para recepción (check-in rápido).
 */
import React, { useMemo } from 'react';
import {
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import type { Appointment, AppointmentResource } from '../domain/types';
import { agenda, agendaDotColor, agendaFonts } from '../domain/agendaTheme';
import { formatMoney } from '../../shared/format/public-api';
import { PaperSheet } from '../../shared/ticket/PaperSheet';
import { ticketColors } from '../../shared/theme/ticketColors';
import { VueltiDurmiendo } from '../../shared/ui/VueltiDurmiendo';

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
}

export function appointmentServiceLabel(
  a: Appointment,
  productNameById: Map<string, string>,
): string | null {
  if (a.items && a.items.length > 0) {
    return a.items.length === 1
      ? a.items[0]!.nameSnapshot
      : `${a.items[0]!.nameSnapshot} + ${a.items.length - 1} más`;
  }
  return productNameById.get(a.productId) ?? null;
}

function metaExtras(a: Appointment): string[] {
  const out: string[] = [];
  if (a.status === 'cancelled') {
    if (a.depositResolutionStatus === 'refunded') out.push('seña devuelta');
    else if (a.depositResolutionStatus === 'kept') out.push('seña retenida');
    else if (a.depositResolutionStatus === 'refund_pending') out.push('devolución pendiente');
    return out;
  }
  if (a.status === 'requested') out.push('sin confirmar');
  if (a.paymentStatus === 'deposit_paid') out.push('seña paga');
  if (a.packageEntitlementId) out.push('paquete');
  const rest =
    a.amountTotal != null && a.amountPaid != null && a.amountTotal > a.amountPaid
      ? a.amountTotal - a.amountPaid
      : null;
  if (rest != null && a.paymentStatus !== 'paid') out.push(`resta ${formatMoney(rest)}`);
  return out;
}

type Props = {
  day: Date;
  appointments: Appointment[];
  resources: AppointmentResource[];
  productNameById: Map<string, string>;
  /** Pref local — los cancelados se ven apagados (ghost). */
  showCancelled: boolean;
  canEdit: boolean;
  selectedAppointmentId?: string | null;
  refreshing?: boolean;
  onRefresh?: () => void;
  onPressAppointment: (a: Appointment) => void;
  /** requested → confirmed (transición directa, como en el detalle). */
  onConfirm: (a: Appointment) => void;
  /** Marca llegada — mismo RPC que el detalle. */
  onArrive: (a: Appointment) => void;
};

export function AppointmentsDayList(props: Props) {
  const resourceById = useMemo(
    () => new Map(props.resources.map((r) => [r.id, r])),
    [props.resources],
  );
  const indexById = useMemo(
    () => new Map(props.resources.map((r, i) => [r.id, i])),
    [props.resources],
  );

  const rows = useMemo(() => {
    const list = props.appointments.filter(
      (a) => props.showCancelled || a.status !== 'cancelled',
    );
    return list.sort(
      (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
    );
  }, [props.appointments, props.showCancelled]);

  return (
    <View style={styles.root}>
      <PaperSheet style={styles.card}>
        <ScrollView
          refreshControl={
            props.onRefresh ? (
              <RefreshControl refreshing={props.refreshing === true} onRefresh={props.onRefresh} />
            ) : undefined
          }
        >
          {rows.length === 0 ? (
            <View style={styles.empty}>
              <VueltiDurmiendo size={80} />
              <Text style={styles.emptyText}>Sin turnos para este día.</Text>
            </View>
          ) : (
            rows.map((a, i) => {
              const res = resourceById.get(a.resourceId);
              const dot = agendaDotColor(res?.color ?? null, indexById.get(a.resourceId) ?? 0);
              const cancelled = a.status === 'cancelled';
              const service = appointmentServiceLabel(a, props.productNameById);
              const extras = metaExtras(a);
              const meta = [service, res?.displayName, ...extras].filter(Boolean).join(' · ');
              const selected = props.selectedAppointmentId === a.id;
              return (
                <View
                  key={a.id}
                  style={[
                    styles.row,
                    i > 0 && styles.rowBorder,
                    cancelled && styles.rowGhost,
                    selected && styles.rowSelected,
                  ]}
                >
                  <TouchableOpacity
                    style={styles.rowHit}
                    activeOpacity={0.8}
                    onPress={() => props.onPressAppointment(a)}
                    accessibilityRole="button"
                    accessibilityLabel={`${formatTime(a.startsAt)} ${a.customerName}`}
                  >
                    <Text style={styles.time}>{formatTime(a.startsAt)}</Text>
                    <View style={[styles.dot, { backgroundColor: dot }]} />
                    <View style={styles.tx}>
                      <View style={styles.nameRow}>
                        <Text
                          style={[styles.name, cancelled && styles.nameGhost]}
                          numberOfLines={1}
                        >
                          {a.customerName}
                        </Text>
                        {a.arrivedAt && !cancelled ? (
                          <Text style={styles.tagOk}>✓ Llegó</Text>
                        ) : null}
                        {a.status === 'completed' ? (
                          <Text style={styles.tagMuted}>Completado</Text>
                        ) : null}
                        {a.status === 'no_show' ? <Text style={styles.tagBad}>No vino</Text> : null}
                        {cancelled ? <Text style={styles.tagMuted}>Cancelado</Text> : null}
                      </View>
                      {meta ? (
                        <Text style={styles.meta} numberOfLines={1}>
                          {meta}
                        </Text>
                      ) : null}
                    </View>
                  </TouchableOpacity>
                  {props.canEdit && a.status === 'requested' ? (
                    <TouchableOpacity
                      style={[styles.action, styles.actionWarn]}
                      onPress={() => props.onConfirm(a)}
                      hitSlop={4}
                      accessibilityRole="button"
                      accessibilityLabel={`Confirmar turno de ${a.customerName}`}
                      testID={`agenda.list.confirm.${a.id}`}
                    >
                      <Text style={[styles.actionText, styles.actionWarnText]}>Confirmar ›</Text>
                    </TouchableOpacity>
                  ) : props.canEdit && a.status === 'confirmed' && !a.arrivedAt ? (
                    <TouchableOpacity
                      style={[styles.action, styles.actionBrand]}
                      onPress={() => props.onArrive(a)}
                      hitSlop={4}
                      accessibilityRole="button"
                      accessibilityLabel={`Marcar llegada de ${a.customerName}`}
                      testID={`agenda.list.arrive.${a.id}`}
                    >
                      <Text style={[styles.actionText, styles.actionBrandText]}>Llegó ›</Text>
                    </TouchableOpacity>
                  ) : null}
                </View>
              );
            })
          )}
        </ScrollView>
      </PaperSheet>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: agenda.bg, padding: 10 },
  card: { flex: 1, borderColor: ticketColors.ruleSoft },
  empty: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32 },
  emptyText: { fontSize: 14, color: agenda.ink2, fontFamily: agendaFonts.sans, marginTop: 10, textAlign: 'center' },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 14,
    paddingVertical: 11,
  },
  rowBorder: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: agenda.border,
  },
  rowGhost: { opacity: 0.55 },
  rowSelected: { backgroundColor: agenda.chooseBg },
  /** Hit-area de la fila (la acción contextual va como botón hermano). */
  rowHit: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  time: {
    width: 46,
    fontSize: 12.5,
    fontWeight: '700',
    color: agenda.ink,
    fontFamily: agendaFonts.mono,
  },
  dot: { width: 9, height: 9, borderRadius: 5, flexShrink: 0 },
  tx: { flex: 1, minWidth: 0, gap: 1 },
  nameRow: { flexDirection: 'row', alignItems: 'center', gap: 6, minWidth: 0 },
  name: {
    fontSize: 13.5,
    fontWeight: '700',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
    flexShrink: 1,
  },
  nameGhost: { textDecorationLine: 'line-through' },
  meta: { fontSize: 11.5, color: agenda.ink2, fontFamily: agendaFonts.sans },
  tagOk: {
    fontSize: 10.5,
    fontWeight: '700',
    color: agenda.confirmed.text,
    fontFamily: agendaFonts.sans,
  },
  tagMuted: { fontSize: 10.5, fontWeight: '600', color: agenda.ink2, fontFamily: agendaFonts.sans },
  tagBad: {
    fontSize: 10.5,
    fontWeight: '700',
    color: agenda.noShow.text,
    fontFamily: agendaFonts.sans,
  },
  action: {
    flexShrink: 0,
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 11,
    paddingVertical: 6,
  },
  actionWarn: { borderColor: agenda.requested.border, backgroundColor: agenda.requested.bg },
  actionWarnText: { color: agenda.requested.text },
  actionBrand: { borderColor: agenda.brand, backgroundColor: agenda.chooseBg },
  actionBrandText: { color: agenda.chooseInk },
  actionText: { fontSize: 12, fontWeight: '700', fontFamily: agendaFonts.sans },
});
